/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ActionGroup, ActionType } from "client-code/actions";
import { PreviewGroup } from "client-code/previews";
import type {
  TActionsRecordingParams,
  TCustomActionList,
  TPreviewServiceGetRecordingPreviewParamsV3
} from "@tivocorp/data-domain-lib";

import { MetadataType, NdvrPlaybackType } from "../enums";
import { GenericErrorCode, PlaybackError } from "../error";
import type {
  IMetadata,
  IMetadataModel,
  INdvrMetadataParams,
  INdvrPlaybackModel,
  TMetadataModelParams
} from "../interfaces";
import { Log } from "../../logging";
import {
  ActionsInteractor,
  PreviewInteractor,
  RecordingsServiceInteractor
} from "../../interactor";

/**
 * Metadata model implementation for NDVR (Network DVR) recording playback.
 *
 * Handles fetching and storing metadata for a single recording:
 * - ACTION_LIST: resolves the recordingId for the given NdvrPlaybackModel
 * - RECORDING_ACTION_LIST: watch/record/onepass/bookmark actions for the recording
 * - PREVIEW / FULL_INFO_PREVIEW: recording offer preview
 * - NDVR_LAST_VIEWED_POSITION: last viewed position for resume
 *
 * NdvrPlaybackType.CONTENT / UNIFIED_COLLECTION are intentionally not supported
 * yet: the current ActionsService only exposes content/collection "screen
 * details" APIs (nested detail/presentation models, not a flat action list),
 * unlike RECORDING which maps 1:1 to the playable id.
 *
 * @implements {IMetadataModel}
 */
export class NdvrMetadataModelImpl implements IMetadataModel {
  private static readonly TAG: string = "NdvrMetadataModelImpl";

  private static readonly RECORDING_ACTION_GROUPS: ActionGroup[] = [
    ActionGroup.Watch,
    ActionGroup.ScheduleRecording,
    ActionGroup.ScheduleOnepass,
    ActionGroup.ScheduleBookmark
  ];

  private static readonly STANDARD_PREVIEW_GROUPS: PreviewGroup[] = [
    PreviewGroup.Metadata,
    PreviewGroup.Linear,
    PreviewGroup.CriticRating,
    PreviewGroup.RecordingStatus
  ];

  private static readonly FULL_PREVIEW_GROUPS: PreviewGroup[] = [
    PreviewGroup.Metadata,
    PreviewGroup.Linear,
    PreviewGroup.CriticRating,
    PreviewGroup.Description,
    PreviewGroup.Credits
  ];

  private readonly mMetadata: IMetadata = {};

  private mError: PlaybackError = new PlaybackError("", "");

  private mRecordingId: string = "";

  private readonly mActionsInteractor: ActionsInteractor =
    new ActionsInteractor();

  private readonly mPreviewInteractor: PreviewInteractor =
    new PreviewInteractor();

  private readonly mRecordingsServiceInteractor: RecordingsServiceInteractor =
    new RecordingsServiceInteractor();

  private isNdvrParams(
    params: TMetadataModelParams
  ): params is INdvrMetadataParams {
    return (
      "ndvrPlaybackModel" in params && params.ndvrPlaybackModel !== undefined
    );
  }

  private async retrieveActionList(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    switch (ndvrPlaybackModel.ndvrType) {
      case NdvrPlaybackType.RECORDING:
        this.mRecordingId = ndvrPlaybackModel.id;
        return;
      case NdvrPlaybackType.CONTENT:
      case NdvrPlaybackType.UNIFIED_COLLECTION:
      default: {
        const errorMessage = `Unsupported NdvrPlaybackType: ${ndvrPlaybackModel.ndvrType}`;
        Log.e(NdvrMetadataModelImpl.TAG, "retrieveActionList()", errorMessage);
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          errorMessage,
          "Recording Unavailable",
          "This recording type is not supported yet."
        );
        throw this.mError;
      }
    }
  }

  private async fetchRecordingActions(): Promise<void> {
    try {
      const params: TActionsRecordingParams = {
        recordingId: this.mRecordingId,
        groups: NdvrMetadataModelImpl.RECORDING_ACTION_GROUPS
      };
      const response: TCustomActionList =
        await this.mActionsInteractor.fetchRecordingActions(params, false);
      this.mMetadata.actions = response.actions;
      this.mMetadata.watchAction = response.actions.find(
        (action) => action.actionType === ActionType.WatchNowAction
      ) as typeof this.mMetadata.watchAction;
    } catch (error) {
      Log.e(NdvrMetadataModelImpl.TAG, "fetchRecordingActions()", error);
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "Failed to fetch recording actions",
        "Actions Unavailable",
        "Unable to retrieve actions for this recording."
      );
      throw this.mError;
    }
  }

  private async fetchRecordingLastViewedPosition(): Promise<void> {
    try {
      const response =
        await this.mRecordingsServiceInteractor.fetchLastViewedPosition({
          recordingId: this.mRecordingId
        });
      this.mMetadata.ndvrLastViewedPosition = response.lastViewedPosition;
    } catch (error) {
      Log.e(
        NdvrMetadataModelImpl.TAG,
        "fetchRecordingLastViewedPosition()",
        error
      );
      // Last viewed position is a resume-affordance, not a playback blocker.
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "Failed to fetch NDVR last viewed position"
      );
      this.mMetadata.ndvrLastViewedPosition = 0;
    }
  }

  private async fetchRecordingOfferPreviewData(
    metadataType: MetadataType.PREVIEW | MetadataType.FULL_INFO_PREVIEW
  ): Promise<void> {
    try {
      const groups =
        metadataType === MetadataType.FULL_INFO_PREVIEW
          ? NdvrMetadataModelImpl.FULL_PREVIEW_GROUPS
          : NdvrMetadataModelImpl.STANDARD_PREVIEW_GROUPS;
      const params: TPreviewServiceGetRecordingPreviewParamsV3 = {
        recordingId: this.mRecordingId,
        groups
      };
      this.mMetadata.offerPreview =
        await this.mPreviewInteractor.fetchRecordingPreviewV3(params, false);
    } catch (error) {
      Log.e(
        NdvrMetadataModelImpl.TAG,
        "fetchRecordingOfferPreviewData()",
        error
      );
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "Failed to fetch recording preview",
        "Preview Unavailable",
        "Unable to retrieve preview information for this recording."
      );
      throw this.mError;
    }
  }

  public getMetadata(): IMetadata {
    return this.mMetadata;
  }

  public getError(): PlaybackError {
    return this.mError;
  }

  public async prepareMetadataByType(
    params: TMetadataModelParams
  ): Promise<void> {
    if (!this.isNdvrParams(params)) {
      const errorMessage = "Invalid params for NdvrMetadataModelImpl";
      Log.e(
        NdvrMetadataModelImpl.TAG,
        "prepareMetadataByType(): ",
        errorMessage
      );
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        errorMessage,
        "Invalid Parameters",
        "The metadata parameters are not valid for NDVR content."
      );
      throw this.mError;
    }

    const { metadataType, ndvrPlaybackModel } = params;

    if (metadataType === MetadataType.ACTION_LIST) {
      await this.retrieveActionList(ndvrPlaybackModel);
    } else if (metadataType === MetadataType.RECORDING_ACTION_LIST) {
      await this.fetchRecordingActions();
    } else if (
      metadataType === MetadataType.PREVIEW ||
      metadataType === MetadataType.FULL_INFO_PREVIEW
    ) {
      await this.fetchRecordingOfferPreviewData(metadataType);
    } else if (metadataType === MetadataType.NDVR_LAST_VIEWED_POSITION) {
      await this.fetchRecordingLastViewedPosition();
    } else {
      const errorMessage = `Unsupported metadata type for NdvrMetadataModelImpl: ${metadataType}`;
      Log.e(
        NdvrMetadataModelImpl.TAG,
        "prepareMetadataByType(): ",
        errorMessage
      );
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        errorMessage
      );
      throw this.mError;
    }
  }
}
